import styled from "@emotion/styled";
import type { NFTPoolOrder } from "@stepfinance/nft";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CheckboxFilter,
  formatBalance,
  IconTextContainer,
  makeSimpleFilters,
  PlatformIcon,
  StepTable,
  TokenIconText,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import { Tooltip } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useState } from "react";

import infoIcon from "../../../assets/info-round-grey.svg";
import { useLanguageContext } from "../../../contexts/language/language";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { PAGE_SIZE_OPTIONS } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

type NFTPoolOrdersTableProps = {
  totalValue: number;
  poolOrders: NFTPoolOrder[];
};

export const NFTPoolOrdersTable: FC<NFTPoolOrdersTableProps> = ({ poolOrders, totalValue }) => {
  const {
    currentLanguageData: {
      common,
      pages: {
        nftGallery: { nftFloorPrice, nftCollection },
        dashboard: {
          modules: { nftMarkets },
        },
      },
    },
  } = useLanguageContext();

  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "starAtlasOpenOrdersPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(poolOrders.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [poolOrders.length, paginationSetting]);

  const columns: StepColumnsType<NFTPoolOrder> = [
    {
      title: common.platform,
      dataIndex: "kind",
      key: "kind",
      filters: makeSimpleFilters(poolOrders.map((order) => order.platform)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.platform.startsWith(value as string),
      filteredValue: filteredInfo.kind ?? null,
      render(_, { platform }) {
        return (
          <IconTextContainer>
            <PlatformIcon names={[platform]} />
            <TokenIconText bold>{platform}</TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a, b) => a.platform.localeCompare(b.platform),
    },
    {
      title: nftCollection,
      dataIndex: "nftCollectionName",
      key: "nftCollectionName",
      className: "column-string",
      render(_, record) {
        return (
          <IconTextContainer>
            <URLIcon src={record.nftImageURL} />
            <TokenIconText bold>{record.nftCollectionName}</TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a, b) => {
        return a.nftCollectionName.localeCompare(b.nftCollectionName);
      },
    },
    {
      title: nftFloorPrice,
      dataIndex: "floorPrice",
      key: "floorPrice",
      className: "column-string",
      render(_, record) {
        return record.floorPrice > 0 ? (
          <CellValue cellType="number">{formatBalance(record.floorPrice, 2)}</CellValue>
        ) : (
          <CellValue cellType="userPositionValue">{record.floorPrice}</CellValue>
        );
      },
      sorter: (a, b) => a.floorPrice - b.floorPrice,
    },
    {
      title: nftMarkets.nftsHeld,
      dataIndex: "nftsHeld",
      key: "nftsHeld",
      className: "column-string",
      render(_, record) {
        return <CellValue cellType="number">{record.nftsHeld}</CellValue>;
      },
      sorter: (a, b) => a.nftsHeld - b.nftsHeld,
    },
    {
      title: nftMarkets.maxBuyCount,
      dataIndex: "maxNftBuyCount",
      key: "maxNftBuyCount",
      className: "column-string",
      render(_, record) {
        return <CellValue cellType="number">{record.maxNftBuyCount}</CellValue>;
      },
      sorter: (a, b) => a.maxNftBuyCount - b.maxNftBuyCount,
    },
    {
      title: nftMarkets.solBalance,
      dataIndex: "solBalance",
      key: "solBalance",
      className: "column-string",
      render(_, record) {
        return record.sharedEscrow ? (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {0}
          </CellValue>
        ) : (
          <CellValue cellType="number">{formatBalance(record.solBalance, 2)}</CellValue>
        );
      },
      sorter: (a, b) => a.solBalance - b.solBalance,
    },
    {
      title: "Profit Avail",
      dataIndex: "mmProfit",
      key: "mmProfit",
      className: "column-string",
      render(_, record) {
        return record.mmProfit ? (
          <CellValue cellType="number">{formatBalance(record.mmProfit, 2)}</CellValue>
        ) : (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {0}
          </CellValue>
        );
      },
      sorter: (a, b) => a.solBalance - b.solBalance,
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      className: "column-string",
      render(_, record) {
        return record.sharedEscrow ? (
          <StyledValueContainer>
            <CellValue cellType="userPositionValue" fontWeight="bold">
              {record.value}
            </CellValue>
            <Tooltip title={nftMarkets.escrowDetail} placement="bottom" overlayStyle={{ width: 200 }}>
              <StyledInfoIcon alt="info icon" src={infoIcon} width={12.8} height={12.8} />
            </Tooltip>
          </StyledValueContainer>
        ) : (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {record.value}
          </CellValue>
        );
      },
      sorter: (a, b) => (a.value ?? 0) - (b.value ?? 0),
    },
  ];

  return (
    <NFTOrdersContainer>
      <ChevronCollapse title={nftMarkets.nftPoolOrders} value={totalValue} isCollapseChild>
        <StepTable
          rowKey={(record, index) => `${record.nftCollectionName}-${record.value}-${index}`}
          onChange={(pagination, filters) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            setFilteredInfo(filters);
          }}
          pagination={pagination}
          columns={columns}
          dataSource={poolOrders}
        />
      </ChevronCollapse>
    </NFTOrdersContainer>
  );
};

const NFTOrdersContainer = styled.div`
  margin-top: 20px;
`;

const StyledInfoIcon = styled.img`
  width: 12.8px;
  height: 12.8px;
  margin-left: 0.5em;
`;

const StyledValueContainer = styled.div`
  align-items: center;
  display: flex;
`;
